import {
  rdsGapClassName,
  type RdsSpacing,
} from '@/shared/styles/rds-spacing';
import { cn } from '@/shared/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

/**
 * RDS Row — flex 행/열 + Spacing 토큰 기반 gap(gutter).
 * Ant Design Row의 gutter와 유사하게, 자식 사이 간격을 RDS spacing으로 준다.
 */
const rowVariants = cva('flex', {
  variants: {
    direction: {
      row: 'flex-row',
      column: 'flex-col',
      'row-reverse': 'flex-row-reverse',
      'column-reverse': 'flex-col-reverse',
    },
    align: {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
      baseline: 'items-baseline',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
      around: 'justify-around',
      evenly: 'justify-evenly',
    },
    wrap: {
      true: 'flex-wrap',
      false: 'flex-nowrap',
    },
  },
  defaultVariants: {
    direction: 'row',
    align: 'stretch',
    justify: 'start',
    wrap: false,
  },
});

type RowElement = 'div' | 'form' | 'section' | 'header' | 'nav';

export interface RowProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'wrap'>,
    VariantProps<typeof rowVariants> {
  /**
   * 자식 사이 간격 (RDS Spacing px).
   * `gutter`와 동일. 기본 16 (Spacing/16).
   */
  gap?: RdsSpacing;
  /** Ant Design 호환 alias — `gap`과 동일 */
  gutter?: RdsSpacing;
  /** 렌더 태그 — 필터 form이면 `form` */
  as?: RowElement;
}

const Row = React.forwardRef<HTMLElement, RowProps>(
  (
    {
      className,
      direction,
      align,
      justify,
      wrap,
      gap,
      gutter,
      as: Comp = 'div',
      ...props
    },
    ref,
  ) => {
    const space = gap ?? gutter ?? 16;

    return (
      <Comp
        ref={ref as never}
        className={cn(
          rowVariants({ direction, align, justify, wrap }),
          rdsGapClassName(space),
          className,
        )}
        {...(props as React.HTMLAttributes<HTMLElement>)}
      />
    );
  },
);
Row.displayName = 'Row';

export interface ColProps extends React.HTMLAttributes<HTMLDivElement> {
  /** flex-grow 비율 (숫자면 `flex-{n}`) */
  flex?: number | string;
  /** true면 flex-1 */
  grow?: boolean;
  /** 최소 너비 0 (truncate 등과 함께) */
  minZero?: boolean;
}

const Col = React.forwardRef<HTMLDivElement, ColProps>(
  ({ className, flex, grow, minZero, style, ...props }, ref) => {
    const flexStyle =
      typeof flex === 'number'
        ? { flex: `${flex} ${flex} 0%`, ...style }
        : typeof flex === 'string'
          ? { flex, ...style }
          : style;

    return (
      <div
        ref={ref}
        className={cn(grow && 'flex-1', minZero && 'min-w-0', className)}
        style={flexStyle}
        {...props}
      />
    );
  },
);
Col.displayName = 'Col';

/** 목록 검색·필터 바 (wrap + items-end + gap 12) */
const FilterRow = React.forwardRef<HTMLElement, Omit<RowProps, 'align' | 'wrap'>>(
  ({ gap = 12, as = 'form', ...props }, ref) => (
    <Row ref={ref} as={as} wrap align="end" gap={gap} {...props} />
  ),
);
FilterRow.displayName = 'FilterRow';

/** 상세 헤더·툴바 (wrap + items-center + gap 16) */
const ToolbarRow = React.forwardRef<
  HTMLElement,
  Omit<RowProps, 'align' | 'wrap'>
>(({ gap = 16, as = 'div', ...props }, ref) => (
  <Row ref={ref} as={as} wrap align="center" gap={gap} {...props} />
));
ToolbarRow.displayName = 'ToolbarRow';

/** 버튼 푸터 (기본 justify-end + gap 8, justify 오버라이드 가능) */
const FooterRow = React.forwardRef<HTMLElement, Omit<RowProps, 'align'>>(
  ({ gap = 8, as = 'div', justify = 'end', ...props }, ref) => (
    <Row
      ref={ref}
      as={as}
      justify={justify}
      align="center"
      gap={gap}
      {...props}
    />
  ),
);
FooterRow.displayName = 'FooterRow';

/** 세로 스택 (폼 필드·섹션) */
const Stack = React.forwardRef<
  HTMLElement,
  Omit<RowProps, 'direction'>
>(({ gap = 16, as = 'div', ...props }, ref) => (
  <Row ref={ref} as={as} direction="column" gap={gap} {...props} />
));
Stack.displayName = 'Stack';

export {
  Row,
  Col,
  FilterRow,
  ToolbarRow,
  FooterRow,
  Stack,
  rowVariants,
};
